/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState } from 'react';
import { Container, useToast } from '@crisellevaldez/speculo-ui';
import { HelpCircle } from 'lucide-react';
import { ScheduleCallModal } from '../components/features/header/ScheduleCallModal';
import { SUPPORT_CONTACT } from '../constants/supportContact';
import { OnboardingProgress } from '../components/features/onboarding/components/OnboardingProgress';
import { StagePreWelcome } from '../components/features/onboarding/stages/StagePreWelcome';
import { Stage0Welcome } from '../components/features/onboarding/stages/Stage0Welcome';
import { Stage1OrganizationSetup } from '../components/features/onboarding/stages/Stage1OrganizationSetup';
import { Stage3RemiSetup } from '../components/features/onboarding/stages/Stage3RemiSetup';
import { Stage4IDXFlow } from '../components/features/onboarding/stages/Stage4IDXFlow';
import { Stage5CRMSetup } from '../components/features/onboarding/stages/Stage5CRMSetup';
import { LastStageGuide } from '../components/features/onboarding/stages/LastStageGuide';
import { TwilioStatusBanner } from '../components/features/onboarding/components/TwilioStatusBanner';
import { MLSStatusBanner } from '../components/features/onboarding/components/MLSStatusBanner';
import { WebsiteStatusBanner } from '../components/features/onboarding/components/WebsiteStatusBanner';
import { IntegrationsStatusBanner } from '../components/features/onboarding/components/IntegrationsStatusBanner';
import { useOnboardingData } from '../components/features/onboarding/hooks/useOnboardingData';
import { useOnboardingSecrets } from '../components/features/onboarding/hooks/useOnboardingSecrets';
import {
  OrganizationSetupData,
  RemiSetupData,
  TwilioSetupData,
  IDXSetupData,
  EmailDomainSetup,
  RemiIdxSetupData,
  CRMSetupData,
} from '../components/features/onboarding/schemas/onboarding.schema';
import { useAuthStore } from '../contexts/auth/useAuthStore';
import { useUser } from '../hooks/useUser';
import { useOrganizationStore } from '../stores/useOrganizationStore';
import { useUpdateOrganizationSettings } from '../hooks/organizations/useUpdateOrganizationSettings';
import { useGeneralKnowledgeBase } from '../hooks/client/useGeneralKnowledgeBase';
import { useQueryClient } from '@tanstack/react-query';
import { clearOrganizationCache, invalidateOrganizationQueries } from '../utils/cacheUtils';
import { useAutoCreateFlows } from '../components/features/onboarding/hooks/useAutoCreateFlows';
import {
  getStageFromSetup,
  ONBOARDING_STAGES,
  toStepperStage,
} from '../components/features/onboarding/utils/onboardingStages';
import {
  getConfirmedSave,
  OrganizationSaveResponse,
  stageCompleted,
} from '../components/features/onboarding/utils/confirmStageSave';
import { SAVE_NOT_CONFIRMED_MESSAGE } from '../components/features/onboarding/utils/savingMessages';
import { useWarnBeforeUnload } from '../components/features/onboarding/hooks/useWarnBeforeUnload';
import { buildGeneralKnowledgeBase } from '../components/features/onboarding/utils/generalKnowledgeBase';
import {
  remiIdxEmailDomain,
  toStoredSiteData,
} from '../components/features/onboarding/utils/remiIdxSite';
import {
  saveIDXDomainRegistrarSecrets,
  saveRemiIdxDomainRegistrarSecrets,
} from '../api/organizationSecrets';

class SaveNotConfirmedError extends Error {
  constructor() {
    super(SAVE_NOT_CONFIRMED_MESSAGE);
  }
}

export const ClientOnboarding: React.FC = () => {
  const { user } = useAuthStore();
  // The onboarding user's profile name ("Same as me" on the MLS step); the auth
  // account often has no display name.
  const { user: profile } = useUser();
  const profileName = [profile?.firstName, profile?.lastName].filter(Boolean).join(' ').trim();
  const { organization, fetchOrganization, setOrganization } = useOrganizationStore();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPreWelcome, setShowPreWelcome] = useState(true);
  const [isScheduleCallModalOpen, setIsScheduleCallModalOpen] = useState(false);
  // Tracks whether user clicked "Start Setup" on Stage 0 (before any backend save)
  const [userHasStarted, setUserHasStarted] = useState(false);

  // Hook for auto-creating flows after Stage 3
  const { createAllFlows } = useAutoCreateFlows();

  // Hook for saving sensitive onboarding data to Secret Manager (not Firestore)
  const { saveBusinessTaxSecrets, saveCRMCredentials } = useOnboardingSecrets();

  // Calculate initial stage based on completed stages in Firebase
  const getInitialStage = (): number =>
    organization ? getStageFromSetup(organization.onboardingSetup) : 0;

  const { onboardingData, saveStage1Data, saveStage3Data, saveStage4Data } =
    useOnboardingData(getInitialStage());

  // firestoreStage drives everything once backend has data (stage > 0).
  // When backend has no data (stage 0), allow userHasStarted to push to stage 1
  // without relying on localStage (which may be stale from cache).
  const firestoreStage = getInitialStage();
  const currentStage = firestoreStage > 0 ? firestoreStage : userHasStarted ? 1 : 0;

  const { updateSettings } = useUpdateOrganizationSettings();

  const { addKnowledgeBase } = useGeneralKnowledgeBase(organization?.id);

  useWarnBeforeUnload(isSubmitting);

  /**
   * Advances the stage from the save response itself: the API returns the row
   * it just committed, so a confirmed `stageN.completed` there is proof of the
   * save. The org in the store is patched from it (not refetched — a lagging
   * read could send the user back a stage) and other org caches refresh in
   * the background. Throws when the response doesn't confirm the save.
   */
  const applyConfirmedStageSave = (response: unknown, stageKey: `stage${number}`) => {
    const confirmed = getConfirmedSave(
      response as OrganizationSaveResponse,
      stageCompleted(stageKey)
    );
    if (!confirmed) throw new SaveNotConfirmedError();
    const current = useOrganizationStore.getState().organization;
    if (current) setOrganization({ ...current, ...confirmed.patch });
    invalidateOrganizationQueries(queryClient);
  };

  const saveErrorMessage = (error: unknown, fallback: string) =>
    error instanceof SaveNotConfirmedError ? error.message : fallback;

  const handleStage1Submit = async (data: OrganizationSetupData) => {
    if (!organization?.id) {
      showToast({
        message: 'No organization found',
      });
      return;
    }

    setIsSubmitting(true);
    try {
      // Save data locally
      saveStage1Data(data);

      const saveResponse = await updateSettings({
        id: organization.id,
        data: {
          org_name: data.org_name,
          org_phone: data.org_phone,
          email: data.email,
          notification_email: data.notification_email,
          client_address: data.client_address,
          timezone: data.timezone,
          onboardingSetup: {
            ...(useOrganizationStore.getState().organization?.onboardingSetup ?? {}),
            stage1: {
              completed: true,
              createdAt: new Date().toISOString(),
            },
          },
        } as any,
      });

      // TODO: Send webhook/Zapier notification
      console.log('Stage 1 saved to Firebase:', data);

      applyConfirmedStageSave(saveResponse, 'stage1');

      // Scroll to top (stage is now updated with fresh data)
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error('Error submitting stage 1:', error);
      showToast({
        message: saveErrorMessage(error, 'Failed to save organization settings'),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStage3Submit = async (data: RemiSetupData & { twilioData?: TwilioSetupData }) => {
    if (!organization?.id) {
      showToast({
        message: 'No organization found',
      });
      return;
    }

    setIsSubmitting(true);
    try {
      // Save data locally
      saveStage3Data(data);

      // Build knowledge base array from form data
      const knowledgeBaseItems = buildGeneralKnowledgeBase(data, organization);

      // Save knowledge base to KnowledgeBases collection
      const kbName = data.kb_name || `${organization.org_name} FAQ`;
      await addKnowledgeBase.mutateAsync({
        kb_name: kbName,
        knowledge_base: knowledgeBaseItems,
        organization_id: organization.id,
        type: 'general',
      });

      // Prepare the update data
      const updateData: any = {
        remi_intro_name: data.remi_intro_name,
        assistant_name: data.assistant_name,
        assistant_name_pronunciation: data.assistant_name_pronunciation,
        call_settings: {
          voice: data.voice || '',
          background_track: data.background_track || '',
          record: true,
        },
        onboardingSetup: {
          ...(useOrganizationStore.getState().organization?.onboardingSetup ?? {}),
          stage3: {
            completed: true,
            createdAt: new Date().toISOString(),
          },
        },
      };

      // If Twilio data is provided, save it as well
      if (data.twilioData) {
        saveStage4Data(data.twilioData);

        // Save EIN/CBN to Secret Manager (if provided)
        if (data.twilioData.ein_cbn) {
          await saveBusinessTaxSecrets(organization.id, {
            ein_cbn: data.twilioData.ein_cbn,
          });
        }

        // Save Twilio data (without sensitive EIN/CBN)
        const { ein_cbn, ...twilioDataWithoutEin } = data.twilioData;
        updateData.onboardingData = {
          ...(useOrganizationStore.getState().organization?.onboardingData ?? {}),
          twilio: {
            ...twilioDataWithoutEin,
            has_ein: Boolean(ein_cbn), // Flag to indicate EIN is stored in Secret Manager
            status: 'pending_verification',
          },
        };
      }

      // Save voice settings and Twilio data to Organization document
      const saveResponse = await updateSettings({
        id: organization.id,
        data: updateData,
      });

      // Webhook is sent from Stage3RemiSetup when Twilio step completes
      console.log('Stage 3 saved to Firebase:', data);

      applyConfirmedStageSave(saveResponse, 'stage3');

      // Fire-and-forget: Start creating flows in background
      // This runs while user continues to Stage 4 (IDX setup)
      // Safety flag prevents duplicate creation if triggered again
      createAllFlows()
        .then((result) => {
          if (result.success && result.flowsCreated.length > 0) {
            console.log('Background flow creation completed:', result.flowsCreated);
          }
        })
        .catch((err) => {
          console.error('Background flow creation failed:', err);
        });

      // Scroll to top (Twilio banner and stage are now updated with fresh data)
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error('Error submitting stage 3:', error);
      showToast({
        message: saveErrorMessage(error, 'Failed to save assistant settings'),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Saves the email domain provider login to Secret Manager (if given) and
   * returns the email domain setup without the password, for Firestore.
   */
  const saveEmailDomainSecrets = async (orgId: string, emailDomain: EmailDomainSetup) => {
    const { password, ...emailDomainWithoutPassword } = emailDomain;
    if (emailDomain.access_method === 'credentials' && password) {
      try {
        await saveIDXDomainRegistrarSecrets(orgId, {
          domain_registrar: emailDomain.provider_other_name || emailDomain.provider,
          registrar_username: emailDomain.username,
          registrar_password: password,
          registrar_email: emailDomain.two_fa_contact_email,
          registrar_phone: emailDomain.two_fa_contact_phone,
        });
      } catch (secretsError) {
        // Same as other onboarding secrets: log, don't block the stage.
        console.error('Error saving domain provider credentials:', secretsError);
      }
    }
    return emailDomainWithoutPassword;
  };

  /**
   * "Create Remi's IDX for me": the original IDX setup (the IDX site is already
   * created by its IDX Site Setup step). Email uses the IDX domain; its DNS
   * records were shown with the IDX record.
   */
  const handleRemiIdxSubmit = async (remiIdx: RemiIdxSetupData) => {
    if (!organization?.id) {
      showToast({
        message: 'No organization found',
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const siteData = remiIdx.site_data as any;

      // IDX domain registrar login, when the client asked for help and hasn't shared access
      if (siteData?.needs_domain_help && !siteData?.domain_access_shared) {
        if (siteData.registrar_username || siteData.registrar_password) {
          try {
            await saveRemiIdxDomainRegistrarSecrets(organization.id, {
              domain_registrar: siteData.domain_registrar,
              registrar_username: siteData.registrar_username,
              registrar_password: siteData.registrar_password,
              registrar_email: siteData.registrar_email,
              registrar_phone: siteData.registrar_phone,
            });
          } catch (secretsError) {
            console.error('Error saving IDX domain registrar credentials:', secretsError);
          }
        }
      }

      const idxData: any = {
        idx_path: 'remi',
        mls_entries: remiIdx.mls_data?.mls_entries || [],
        status: 'pending_approval',
      };
      if (siteData) idxData.site_data = toStoredSiteData(siteData);

      const saveResponse = await updateSettings({
        id: organization.id,
        data: {
          onboardingData: {
            ...(useOrganizationStore.getState().organization?.onboardingData ?? {}),
            idx: idxData,
            email_domain: remiIdxEmailDomain(siteData),
          },
          onboardingSetup: {
            ...(useOrganizationStore.getState().organization?.onboardingSetup ?? {}),
            stage4: {
              completed: true,
              createdAt: new Date().toISOString(),
            },
          },
        } as any,
      });

      applyConfirmedStageSave(saveResponse, 'stage4');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error('Error submitting stage 4 (Remi IDX):', error);
      showToast({
        message: error instanceof Error ? error.message : 'Failed to save IDX setup',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStage4Submit = async (data: IDXSetupData) => {
    if (!organization?.id) {
      showToast({
        message: 'No organization found',
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const emailDomainWithoutPassword = await saveEmailDomainSecrets(
        organization.id,
        data.email_domain
      );

      const saveResponse = await updateSettings({
        id: organization.id,
        data: {
          onboardingData: {
            ...(useOrganizationStore.getState().organization?.onboardingData ?? {}),
            idx: { idx_path: 'own', idx_display_url: data.idx_display_url },
            // Without the password — provider credentials are saved to Secret Manager below.
            email_domain: emailDomainWithoutPassword,
          },
          onboardingSetup: {
            ...(useOrganizationStore.getState().organization?.onboardingSetup ?? {}),
            stage4: {
              completed: true,
              createdAt: new Date().toISOString(),
            },
          },
        } as any,
      });

      console.log('Stage 4 saved to Firebase:', data);

      applyConfirmedStageSave(saveResponse, 'stage4');

      // Scroll to top (stage is now updated with fresh data)
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error('Error submitting stage 4:', error);

      showToast({
        message:
          error instanceof Error ? error.message : 'Failed to save IDX setup',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStage5Submit = async (data: CRMSetupData) => {
    if (!organization?.id) {
      showToast({
        message: 'No organization found',
      });
      return;
    }

    setIsSubmitting(true);
    try {
      // Save CRM credentials to Secret Manager (if any non-FUB CRMs with credentials)
      await saveCRMCredentials(organization.id, data.crm_entries);

      // Build CRM entries array for onboardingData (without sensitive fields)
      // Sensitive data is now stored in Secret Manager, not Firestore
      const crmEntries = data.crm_entries.map((entry) => ({
        crm_type: entry.crm_type,
        crm_other_name: entry.crm_other_name,
        ai_disclaimer_acknowledged: Boolean(entry.ai_disclaimer_acknowledged),
        // Every CRM, Rollout ones included, has a 2FA contact.
        two_fa_contact_type: entry.two_fa_contact_type,
        two_fa_contact_name: entry.two_fa_contact_name,
        two_fa_contact_email: entry.two_fa_contact_email,
        two_fa_contact_phone: entry.two_fa_contact_phone,
        // Store non-sensitive info in Firestore
        ...(entry.crm_type !== 'fub' && {
          login_url: entry.login_url,
          // Note: Passwords and API keys are now stored in Secret Manager
          has_credentials: Boolean(
            entry.super_admin_api_key || entry.super_admin_username || entry.super_admin_password
          ),
        }),
        // Brivity-specific non-sensitive info
        ...(entry.crm_type === 'brivity' && {
          brivity_website_login_url: entry.brivity_website_login_url,
          has_brivity_credentials: Boolean(entry.brivity_username || entry.brivity_password),
        }),
        status: entry.crm_type === 'fub' ? 'configured' : 'pending_setup',
      }));

      // Save CRM data to Organization document with onboarding tracking
      const saveResponse = await updateSettings({
        id: organization.id,
        data: {
          onboardingData: {
            ...(useOrganizationStore.getState().organization?.onboardingData ?? {}),
            crm: {
              crm_entries: crmEntries,
            },
            // Persist consent acknowledgment (same field IntegrationsModal reads)
            ...(data.crm_entries.some((e) => e.ai_disclaimer_acknowledged) && {
              crm_consent_acknowledged: true,
              crm_consent_acknowledged_at: new Date().toISOString(),
            }),
          },
          onboardingSetup: {
            ...(useOrganizationStore.getState().organization?.onboardingSetup ?? {}),
            stage5: {
              completed: true,
              createdAt: new Date().toISOString(),
            },
          },
        } as any,
      });

      console.log('Stage 5 saved to Firebase (secrets saved separately):', data);

      applyConfirmedStageSave(saveResponse, 'stage5');

      // Scroll to top (stage is now updated with fresh data)
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error('Error submitting stage 5:', error);
      showToast({
        message: saveErrorMessage(error, 'Failed to save CRM setup'),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderCurrentStage = () => {
    // Show loading while organization is being fetched
    if (!organization) {
      return (
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading...</p>
          </div>
        </div>
      );
    }

    switch (currentStage) {
      case 0:
        return (
          <Stage0Welcome
            userName={user?.displayName || undefined}
            onStart={() => setUserHasStarted(true)}
          />
        );
      case 1:
        return (
          <Stage1OrganizationSetup
            onNext={handleStage1Submit}
            initialData={
              onboardingData.stage1 || {
                org_name: organization?.org_name || '',
                org_phone: organization?.org_phone || '',
                email: organization?.email || '',
                notification_email: (organization as any)?.notification_email || '',
                timezone: organization?.timezone || '',
                client_address: {
                  street: organization?.client_address?.street || '',
                  city: organization?.client_address?.city || '',
                  state: organization?.client_address?.state || '',
                  zip: organization?.client_address?.zip || '',
                  country: (organization?.client_address as any)?.country || ('US' as const),
                },
              }
            }
            isLoading={isSubmitting}
          />
        );
      case 3: {
        // Legacy orgs may have a domain from the retired Website stage
        const websiteUrlDefault =
          organization.onboardingData?.website?.domain_name ||
          (organization as any)?.business_website ||
          '';

        return (
          <Stage3RemiSetup
            onNext={handleStage3Submit}
            initialData={{
              remi_intro_name: (organization?.remi_intro_name as string) || '',
              assistant_name: (organization?.assistant_name as string) || 'Remi',
              assistant_name_pronunciation:
                (organization?.assistant_name_pronunciation as string) || '',
              voice: (organization?.call_settings?.voice as string) || '',
              background_track: (organization?.call_settings?.background_track as string) || '',
              kb_name:
                ((organization as any)?.onboardingData?.remi?.kb_name as string) ||
                `${organization?.org_name || 'Organization'} FAQ`,
              service_type:
                ((organization as any)?.onboardingData?.remi?.service_type as string) || '',
              service_areas:
                ((organization as any)?.onboardingData?.remi?.service_areas as string) || '',
              why_work_with_you:
                ((organization as any)?.onboardingData?.remi?.why_work_with_you as string) || '',
              website_url:
                ((organization as any)?.onboardingData?.remi?.website_url as string) ||
                websiteUrlDefault,
              custom_questions:
                ((organization as any)?.onboardingData?.remi?.custom_questions as Array<{
                  question: string;
                  answer: string;
                }>) || [],
            }}
            initialTwilioData={organization.onboardingData?.twilio || onboardingData.stage4}
            isLoading={isSubmitting}
            organizationName={organization?.org_name || ''}
            officeAddress={
              onboardingData.stage1?.client_address || {
                street: organization?.client_address?.street || '',
                city: organization?.client_address?.city || '',
                state: organization?.client_address?.state || '',
                zip: organization?.client_address?.zip || '',
                country: (organization?.client_address?.country as 'US' | 'Canada') || 'US',
              }
            }
          />
        );
      }
      case 4:
        return (
          <Stage4IDXFlow
            onSubmitOwnIdx={handleStage4Submit}
            onSubmitRemiIdx={handleRemiIdxSubmit}
            remiIdxSiteCreated={organization.onboardingData?.idx_site_completed === true}
            initialData={{
              ...organization.onboardingData?.idx,
              email_domain: organization.onboardingData?.email_domain,
            }}
            isLoading={isSubmitting}
            userEmail={profile?.email || user?.email || ''}
            userName={profileName || user?.displayName || ''}
          />
        );
      case 5:
        return (
          <Stage5CRMSetup
            onNext={handleStage5Submit}
            initialData={(organization as any)?.onboardingData?.crm || {}}
            isLoading={isSubmitting}
            organization={organization}
            onFubSuccess={async () => {
              // Refresh data after FUB setup
              await clearOrganizationCache(queryClient, fetchOrganization);
            }}
          />
        );
      case 6:
        return (
          <LastStageGuide
            organizationName={organization?.org_name || 'your organization'}
            userName={user?.displayName || undefined}
            onComplete={async () => {
              // Mark stage 6 (review) as complete in Firebase
              if (organization?.id) {
                try {
                  await updateSettings({
                    id: organization.id,
                    data: {
                      onboardingSetup: {
                        ...(useOrganizationStore.getState().organization?.onboardingSetup ?? {}),
                        stage6: {
                          completed: true,
                          createdAt: new Date().toISOString(),
                        },
                        revive_tour_completed: false,
                      },
                      isOnboardingComplete: true,
                      // Keeps the main-app support contact on SUPPORT_CONTACT
                      // until Client Admin marks onboarding complete.
                      onboardingData: {
                        ...(useOrganizationStore.getState().organization?.onboardingData ?? {}),
                        onboarding_complete:
                          useOrganizationStore.getState().organization?.onboardingData
                            ?.onboarding_complete ?? { completed: false },
                      },
                    } as any,
                  });
                  // Clear caches so app knows onboarding is complete
                  await clearOrganizationCache(queryClient, fetchOrganization);
                } catch (error) {
                  console.error('Error completing onboarding:', error);
                }
              }
            }}
          />
        );
      default:
        return null;
    }
  };

  // Show pre-welcome full screen image first (only for new users at Stage 0)
  if (showPreWelcome && currentStage === 0) {
    return <StagePreWelcome onContinue={() => setShowPreWelcome(false)} />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-yellow-50 dark:from-gray-950 dark:to-gray-900">
      {/* Status Banners - sticky container for all banners */}
      <div className="sticky top-0 z-40">
        <TwilioStatusBanner organization={organization} />
        <MLSStatusBanner organization={organization} />
        <WebsiteStatusBanner organization={organization} />
        <IntegrationsStatusBanner organization={organization} />
      </div>

      {currentStage > 0 && (
        <>
          {/* Header */}
          <div className="bg-white dark:bg-gray-900 py-8 ">
            <Container>
              <div className="flex flex-col items-center gap-3">
                {/*<img src="/speculo.ai-black.png" alt="Speculo.ai" className="h-12 w-auto" /> */}

                <div className="flex flex-wrap items-center justify-center gap-1">
                  <h1 className="text-2xl sm:text-3xl font-bold text-black dark:text-white">
                    Welcome to{' '}
                  </h1>
                  <img
                    src="/speculo.ai-black-with-text.png"
                    alt="Speculo.ai"
                    className="h-7 sm:h-8 md:h-10 w-auto dark:invert"
                  />
                </div>
                <p className="text-center text-gray-600 dark:text-gray-400 max-w-2xl">
                  Let's get your account set up in a few simple steps
                </p>
              </div>
            </Container>
          </div>

          {/* Progress Tracker */}
          <OnboardingProgress
            currentStage={toStepperStage(currentStage)}
            totalStages={ONBOARDING_STAGES.length}
            stages={ONBOARDING_STAGES}
            organization={organization}
          />
        </>
      )}

      {/* Current Stage Content */}
      <div>{renderCurrentStage()}</div>

      {/* Floating Help Button */}
      <button
        onClick={() => setIsScheduleCallModalOpen(true)}
        className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-50 p-3 sm:p-4 bg-yellow-400 hover:bg-yellow-500 rounded-full shadow-lg hover:shadow-xl transition-all duration-200 group"
        title="Have any questions? Schedule a call"
      >
        <HelpCircle className="w-5 h-5 sm:w-6 sm:h-6 text-black" />
      </button>

      {/* Schedule Call Modal */}
      <ScheduleCallModal
        isOpen={isScheduleCallModalOpen}
        onClose={() => setIsScheduleCallModalOpen(false)}
        supportContact={SUPPORT_CONTACT}
      />
    </div>
  );
};
